import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useRealtimeChannelState, useRealtimeEvent } from 'services';
import type { CommentState, EmbeddedTopic } from './commentStore';
import { CommentTopicStore } from './CommentTopicStore';
import { commentsCreated, commentsDeleted } from './realtimeDefinitions';

export type CommentTopic = {
  store: CommentTopicStore;
  state: CommentState;
  objectKey: string;
};

/**
 * The sole owner of a topic's store. It deliberately starts no network activity:
 * opening a stream is what builds a window. What it does own is the channel
 * interest, so a collapsed badge still learns that comments arrived.
 */
export function useCommentTopic(topic: EmbeddedTopic): CommentTopic {
  const ref = useRef<CommentTopicStore>();
  if (!ref.current || ref.current.objectKey !== topic.object_key) {
    ref.current = new CommentTopicStore(topic);
  }
  const store = ref.current;

  // Closes whichever instance this hook is holding, including one a key change
  // replaced, so nothing in flight outlives it.
  useEffect(() => () => store.close(), [store]);

  useEffect(() => {
    store.seed(topic);
  }, [store, topic]);

  // Realtime is the only thing that changes a closed topic. A deployment with
  // realtime disabled never authorizes a channel, so this is inert rather than
  // conditional. Interests are refcounted per channel key, so a mounted stream
  // shares this subscription instead of opening a second one.
  useRealtimeEvent(store.objectKey, commentsCreated, store.onCreated);
  useRealtimeEvent(store.objectKey, commentsDeleted, store.onDeleted);

  const channelState = useRealtimeChannelState(store.objectKey);
  useEffect(() => {
    if (channelState === 'subscribed') {
      store.resubscribed();
    }
  }, [store, channelState]);

  return {
    store,
    state: useSyncExternalStore(store.subscribe, store.getState),
    objectKey: store.objectKey,
  };
}
